import { z } from 'zod';
import { ActionType } from '../models/action-type.js';
import { VOICE_OPTIONS, VOICE_SETTINGS_DEFAULTS, } from '../models/agent-voice-settings-constants.js';
import { ACTION_BASE_EVENTS, ACTION_TYPE_EVENTS, actionNodeSchema, } from './action-node.js';
import { equationConditionSchema } from './equation.js';
import { snakeKey as eventKey } from './keys.js';
import { formConfigurationSchema, signalSchema } from './form-node.js';
import { ownArrowsBeforeShape } from './flow-precedence.js';
import { preGreetingConditionSchema, startGreetingShape, } from './pre-greeting.js';
import { startConversationNodeSchema } from './start-conversation-node.js';
import { TRANSFER_LIFECYCLE_AUDIT_EVENTS, TRANSFER_NODE_EVENTS, transferNodeSchema, } from './transfer-node.js';
/**
 * The graph an operator authors: nodes of two types, and edges that move the
 * conversation when the condition they carry holds.
 *
 * This is the shape the editor sends, the API stores as rows, and the worker
 * receives assembled at the start of a call. It is the graph's content only:
 * which graph it is, whose it is and when it changed belong to the record
 * around it. Nodes and edges carry ids so a save keeps them and the call's
 * event stream can point back at what fired.
 */
/**
 * Capabilities a conversation node can switch on, settings per row in params.
 * A skill is a tool with settings; there is one list, not two. These are the
 * graph's own names: an agent's `SCHEDULING` skill books a tenant showing, and
 * is called that here because the graph has two scheduling capabilities.
 */
export const NODE_TOOL_TYPES = [
    'SWITCH_LANGUAGE',
    'GET_PROPERTY_LISTINGS',
    'VOICEMAIL_DETECTION',
    'TENANT_SHOWING_SCHEDULING',
    'OWNER_MEETING_SCHEDULING',
];
/** Events each capability raises on its own. */
export const TOOL_EVENTS = {
    SWITCH_LANGUAGE: [],
    GET_PROPERTY_LISTINGS: [],
    VOICEMAIL_DETECTION: ['voicemail_detected'],
    TENANT_SHOWING_SCHEDULING: ['tour_scheduled'],
    OWNER_MEETING_SCHEDULING: ['meeting_booked'],
};
/** Events the runtime raises itself. */
export const RUNTIME_EVENTS = {
    SESSION_STARTED: 'session_started',
    TEXT_DELIVERED: 'text_delivered',
    CONVERSATION_STARTED: 'conversation_started',
    CONVERSATION_ENDED: 'conversation_ended',
};
export const RUNTIME_EVENT_NAMES = Object.values(RUNTIME_EVENTS);
/**
 * Events the per-turn scratchpad detector infers from caller speech when the
 * graph lists them in `watchEvents`. Any node can route on them via an
 * ordinary event-condition edge.
 */
export const SCRATCHPAD_EVENTS = {
    DO_NOT_CALL: 'do_not_call',
    NOT_INTERESTED: 'not_interested',
    HANDOFF_REQUESTED: 'handoff_requested',
};
export const SCRATCHPAD_EVENT_NAMES = Object.values(SCRATCHPAD_EVENTS);
/** Author-facing copy for the watchEvents picker. */
export const SCRATCHPAD_EVENT_LABELS = {
    do_not_call: 'Do not call',
    not_interested: 'Not interested',
    handoff_requested: 'Asks for a human',
};
export const SCRATCHPAD_EVENT_DESCRIPTIONS = {
    do_not_call: 'Caller asks to be removed from the list or opts out of future calls.',
    not_interested: 'Caller declines the offer without opting out — not interested now.',
    handoff_requested: 'Caller asks for a human or a callback instead of continuing.',
};
const ALL_ACTION_TYPES = Object.freeze(Object.values(ActionType));
/**
 * Every event the platform can raise, keyed by name. More than one tool can
 * raise the same event (both scheduling tools raise `meeting_booked`); the
 * `tools` list collects them. Base action events register as emittable by
 * every action type; action-type-specific events register against the one
 * type that emits them.
 */
export const GRAPH_EVENT_REGISTRY = (() => {
    const registry = new Map();
    for (const name of RUNTIME_EVENT_NAMES) {
        registry.set(name, { origin: 'runtime' });
    }
    for (const name of SCRATCHPAD_EVENT_NAMES) {
        registry.set(name, { origin: 'scratchpad' });
    }
    for (const tool of NODE_TOOL_TYPES) {
        for (const name of TOOL_EVENTS[tool]) {
            const existing = registry.get(name);
            if (existing && existing.origin !== 'tool') {
                throw new Error(`Event "${name}" is claimed by ${existing.origin} and by tool ${tool}; events must belong to exactly one origin.`);
            }
            const tools = existing?.origin === 'tool' ? existing.tools : [];
            registry.set(name, { origin: 'tool', tools: [...tools, tool] });
        }
    }
    for (const name of ACTION_BASE_EVENTS) {
        const existing = registry.get(name);
        if (existing && existing.origin !== 'action') {
            throw new Error(`Event "${name}" is claimed by ${existing.origin} and by base action events; events must belong to exactly one origin.`);
        }
        registry.set(name, { origin: 'action', actionTypes: ALL_ACTION_TYPES });
    }
    for (const actionType of Object.keys(ACTION_TYPE_EVENTS)) {
        for (const name of ACTION_TYPE_EVENTS[actionType]) {
            const existing = registry.get(name);
            if (existing && existing.origin !== 'action') {
                throw new Error(`Event "${name}" is claimed by ${existing.origin} and by action ${actionType}; events must belong to exactly one origin.`);
            }
            const actionTypes = existing?.origin === 'action' ? existing.actionTypes : [];
            registry.set(name, {
                origin: 'action',
                actionTypes: [...actionTypes, actionType],
            });
        }
    }
    for (const name of TRANSFER_NODE_EVENTS) {
        const existing = registry.get(name);
        if (existing && existing.origin !== 'transfer') {
            throw new Error(`Event "${name}" is claimed by ${existing.origin} and by the transfer node; events must belong to exactly one origin.`);
        }
        registry.set(name, { origin: 'transfer' });
    }
    for (const name of TRANSFER_LIFECYCLE_AUDIT_EVENTS) {
        const existing = registry.get(name);
        if (existing && existing.origin !== 'transfer-audit') {
            throw new Error(`Event "${name}" is claimed by ${existing.origin} and by transfer-audit; events must belong to exactly one origin.`);
        }
        registry.set(name, { origin: 'transfer-audit' });
    }
    return registry;
})();
const uuid = z.string().uuid();
/**
 * A list of knowledge base ids is a set. Naming the same base twice is the
 * same instruction said twice, so the duplicate is dropped rather than
 * refused: it carries no meaning the author could have intended, and a row
 * per mention is a key the database will not store.
 */
const knowledgeBaseIds = z
    .array(z.string().min(1))
    .default([])
    .transform(ids => [...new Set(ids)]);
export const nodeToolSchema = z.object({
    tool: z.enum(NODE_TOOL_TYPES),
    /** Settings in the shape the capability declares; see skills/<type>. */
    params: z.record(z.unknown()).optional(),
});
/**
 * Author-visible layout for one node. Null means the canvas falls back to
 * dagre. Non-null means the author moved the node and wants it kept there.
 */
export const nodeLayoutMetadataSchema = z
    .object({
    position: z.object({
        x: z.number(),
        y: z.number(),
    }),
})
    .nullable();
/**
 * Author-visible layout for one edge. All three fields are optional so a
 * stored edge can carry just a label position (operator-dragged chip), or
 * just the End pill position (an edge to End doubling as the carrier for
 * that pill, since End is not persisted as a row), or any combination.
 */
export const edgeLayoutMetadataSchema = z
    .object({
    labelPos: z
        .object({
        x: z.number(),
        y: z.number(),
    })
        .optional(),
    /** Was the Start pill position, back when Start had no row of its own.
     *  Start is a node now and keeps its position on that row, so nothing
     *  writes this. Kept so a graph saved before the change still parses. */
    sourceNodePos: z
        .object({
        x: z.number(),
        y: z.number(),
    })
        .optional(),
    /** Set on any edge whose target is End to store the End pill position
     *  — End is implicit. First edge-to-End found on load wins. */
    targetNodePos: z
        .object({
        x: z.number(),
        y: z.number(),
    })
        .optional(),
})
    .nullable();
const nodeBase = {
    id: uuid,
    title: z.string().min(1).max(120),
    /** Display order within the graph. */
    position: z.number().int().nonnegative(),
    layoutMetadata: nodeLayoutMetadataSchema.optional(),
};
/** What a node of each type holds, stored as one typed value on the node row. */
export const conversationConfigurationSchema = z.object({
    prompt: z.string().min(1).max(20000),
    /**
     * What the node listens for while the call is in it. Each reading raises
     * `signal_resolved`, and an equation row can read it. Absent means the node
     * listens for nothing.
     */
    signals: z.array(signalSchema).max(32).optional(),
    /**
     * When true, the runtime fires an unprompted LLM turn on entry so the
     * agent opens the exchange. Lives in the configuration (not just at the
     * node level) so the DAO's conversationConfigurationSchema.parse into
     * the JSON column preserves it on save and read-back.
     */
    speaksOnEntry: z.boolean().optional(),
    ...startGreetingShape,
    ...ownArrowsBeforeShape,
});
export const sayConfigurationSchema = z.object({
    /** Delivered as written, then the runtime raises text_delivered. */
    text: z.string().min(1).max(4000),
});
/**
 * A node's knowledge is the graph's, adjusted. It adds what the graph does not
 * give it and turns off what it does, so a knowledge base added to the graph
 * later reaches every node that has not said otherwise.
 *
 * The pair replaces an inherit switch, which could say "all of them" or "none
 * of them" and nothing in between. The case that matters is one node that must
 * not read one knowledge base, and the switch could only express that by
 * copying the rest, which then stopped tracking the graph for good.
 */
export const conversationNodeSchema = z.object({
    ...nodeBase,
    type: z.literal('conversation'),
    ...conversationConfigurationSchema.shape,
    tools: z.array(nodeToolSchema).default([]),
    /** Knowledge this node reads on top of the graph's. */
    knowledgeBaseIds,
    /** Knowledge the graph gives it that this node does not read. */
    excludedKnowledgeBaseIds: knowledgeBaseIds,
});
/**
 * A block of structured questions; see form-node.ts for its items. Routing
 * lives on `edges[]`, as equation rows that read the form's results and
 * answers. Its tools and knowledge, as
 * on a conversation node, serve the replies it gives when the caller steps
 * off the questions.
 */
export const formNodeSchema = z.object({
    ...nodeBase,
    type: z.literal('form'),
    ...formConfigurationSchema.shape,
    tools: z.array(nodeToolSchema).default([]),
    /** Knowledge this node reads on top of the graph's. */
    knowledgeBaseIds,
    /** Knowledge the graph gives it that this node does not read. */
    excludedKnowledgeBaseIds: knowledgeBaseIds,
});
export const sayNodeSchema = z.object({
    ...nodeBase,
    type: z.literal('say'),
    ...sayConfigurationSchema.shape,
});
/** Both texts persist so toggling mode keeps each draft intact. */
export const endConversationSignOffSchema = z
    .object({
    mode: z.enum(['prompt', 'exact']),
    promptText: z.string().max(4000).default(''),
    exactText: z.string().max(4000).default(''),
})
    .nullable()
    .default(null);
export const endConversationConfigurationSchema = z.object({
    signOff: endConversationSignOffSchema,
});
export const endConversationNodeSchema = z.object({
    ...nodeBase,
    type: z.literal('end_conversation'),
    ...endConversationConfigurationSchema.shape,
});
export const graphNodeSchema = z.discriminatedUnion('type', [
    conversationNodeSchema,
    sayNodeSchema,
    formNodeSchema,
    startConversationNodeSchema,
    endConversationNodeSchema,
    actionNodeSchema,
    transferNodeSchema,
]);
/**
 * Nodes that complete on their own: a say node plays its
 * line, a transfer node gets an outcome, an action node runs its actions. The
 * edges out of one are read once it completes, and may have no condition.
 */
export const COMPLETING_NODE_TYPES = [
    'say',
    'transfer',
    'action',
];
/**
 * What an edge waits for: one event the platform raised, or an equation. An
 * event holds once it was raised: a graph event (what the caller said) in the
 * node visit it was raised in, a node event by the node the edge leaves (any
 * node, for an edge from anywhere). The editor stores an equation of one event
 * with no node as that event (see `canonicalCondition`).
 */
export const eventConditionSchema = z.object({
    type: z.literal('event'),
    /** A key from the registry above. */
    event: eventKey,
});
export const graphConditionSchema = z.discriminatedUnion('type', [
    eventConditionSchema,
    equationConditionSchema,
    preGreetingConditionSchema,
]);
export const graphEdgeSchema = z.object({
    id: uuid,
    /** Null means from anywhere. The entry edge is from nowhere too. */
    fromNodeId: uuid.nullable(),
    /** Null means the conversation ends. */
    toNodeId: uuid.nullable(),
    condition: graphConditionSchema,
    /** Drawn order. When two edges out of one node open on one turn, the lower wins. */
    position: z.number().int().nonnegative(),
    /** A name the author gives the edge. The editor shows it on the arrow. */
    label: z.string().trim().min(1).max(255).optional(),
    layoutMetadata: edgeLayoutMetadataSchema.optional(),
});
/** The entry edge: the one edge that waits for session_started. It leads to Start. */
export function isEntryEdge(edge) {
    return (edge.condition.type === 'event' &&
        edge.condition.event === RUNTIME_EVENTS.SESSION_STARTED);
}
/** A pre-greeting edge: out of Start, on a pre-greeting condition. */
export function isPreGreeting(edge) {
    return edge.condition.type === 'pre_greeting';
}
/**
 * What a node actually reads. The one place this is worked out, so the editor,
 * the API and whatever retrieves knowledge cannot disagree about it.
 */
export function effectiveKnowledgeBaseIds(graphKnowledgeBaseIds, node) {
    if (node.type !== 'conversation' && node.type !== 'form')
        return [];
    const excluded = new Set(node.excludedKnowledgeBaseIds);
    const effective = graphKnowledgeBaseIds.filter(id => !excluded.has(id));
    for (const id of node.knowledgeBaseIds) {
        if (!excluded.has(id) && !effective.includes(id))
            effective.push(id);
    }
    return effective;
}
/**
 * The literal lines of a graph (say text, verbatim form items) in other
 * languages. Keyed by language code, then by line id: the node id for a say
 * node, `nodeId/itemId` for a form item. `linesHash` names the set of English
 * lines they were made from, so a stale translation is never used.
 */
export const graphTranslationsSchema = z.object({
    linesHash: z.string().min(1),
    languages: z.record(z.string().min(2).max(8), z.record(z.string().min(1), z.string())),
});
/**
 * Voices a graph can speak in: the same list the classic agents offer, by
 * the vendor's voice id.
 */
export const GRAPH_VOICE_IDS = VOICE_OPTIONS.map(v => v.id);
/** What a graph with no voice set speaks in. */
export const DEFAULT_GRAPH_VOICE = VOICE_SETTINGS_DEFAULTS.voiceModelId;
/**
 * How the graph sounds when it runs as a call. Only the voice runtime reads
 * these; another channel running the same graph ignores them.
 */
export const graphVoiceSettingsSchema = z.object({
    /** The voice the call speaks in. Absent means the default voice. */
    voice: z.enum(GRAPH_VOICE_IDS).optional(),
});
/**
 * What starts a graph. The author picks one of the company's triggers, and an
 * event carrying that trigger's name runs the graph.
 *
 * Drawn at the loose end of the entry edge, where that edge has no source. So
 * "the trigger comes before Start" on the canvas and "the entry edge leads to
 * Start" in the data describe the same one edge.
 */
export const graphTriggerSchema = z.object({
    /**
     * The `Trigger` row this graph answers. Null for an incoming conversation,
     * which is matched by direction and so has no row. The event name is what
     * dispatch matches either way.
     */
    triggerId: z.string().min(1).max(64).nullable().default(null),
    /** What an inbound event is matched against. */
    eventName: z.string().min(1).max(255),
});
export const graphSchema = z.object({
    name: z.string().min(1).max(255),
    /** Instructions that apply at every node. */
    persona: z.string().max(4000).default(''),
    /**
     * What starts the graph. Absent or null means nothing does yet — a graph is
     * authored before it is subscribed, so this is optional on the way in.
     */
    trigger: graphTriggerSchema.nullable().optional(),
    /** Absent means every voice setting is its default. */
    voiceSettings: graphVoiceSettingsSchema.optional(),
    active: z.boolean().default(true),
    /** Knowledge every conversation node inherits unless it opts out. */
    knowledgeBaseIds,
    nodes: z.array(graphNodeSchema).min(1),
    edges: z.array(graphEdgeSchema).min(1),
    /**
     * Scratchpad events the per-turn detector should infer from caller speech
     * (do_not_call, not_interested, handoff_requested). Empty = detector off.
     * Any node can then route on these via a plain event-condition edge.
     */
    watchEvents: z
        .array(z.enum(SCRATCHPAD_EVENT_NAMES))
        .default([])
        .transform(names => [...new Set(names)]),
    /** Absent means none; the worker then speaks every line as authored. */
    translations: graphTranslationsSchema.optional(),
    /**
     * The company's words and how to say them, attached by the API when it
     * hands the graph to the worker. Absent means the built-in rules only.
     */
    pronunciations: z.record(z.string(), z.string()).optional(),
});
//# sourceMappingURL=graph.js.map